/**
 * Creates or updates tasks via the /api/tasks endpoint from chat voice actions.
 * Supports creating new tasks with subtasks, and adding subtasks to existing tasks.
 */

import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import type { Task } from '@/types/tasks';
import type { VoiceActionItem } from '@/types/voice-transcription';

type TaskResponse = { task: Task };

export class ChatTaskCreator {
  /**
   * Create a new task from a voice action item, including optional subtasks.
   */
  static async createTask(item: VoiceActionItem): Promise<Task> {
    const subtasks = item.subtasks?.map((s, index) => ({
      id: `sub-${Date.now()}-${index}`,
      title: s.title,
      completed: false,
    }));

    const response = await authenticatedFetch.fetch('/api/tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: item.title,
        date: item.date ?? undefined,
        time: item.time ?? undefined,
        description: item.description ?? undefined,
        subtasks: subtasks?.length ? subtasks : undefined,
        metadata: { labels: ['chat-created'] },
      }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = (body as { error?: string }).error ?? `Request failed: ${response.status}`;
      throw new Error(msg);
    }

    const data: TaskResponse = await response.json();
    return data.task;
  }

  /**
   * Add subtasks to an existing task by merging with its current subtasks.
   */
  static async addSubtasksToTask(
    taskId: string,
    newSubtasks: { title: string }[],
  ): Promise<Task> {
    // Fetch current task to preserve existing subtasks
    const getResponse = await authenticatedFetch.fetch(`/api/tasks/${taskId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!getResponse.ok) {
      throw new Error(`Failed to fetch task: ${getResponse.status}`);
    }

    const { task: existingTask }: TaskResponse = await getResponse.json();
    const existingSubtasks = existingTask.subtasks ?? [];

    const mergedSubtasks = [
      ...existingSubtasks,
      ...newSubtasks.map((s, index) => ({
        id: `sub-${Date.now()}-${index}`,
        title: s.title,
        completed: false,
      })),
    ];

    const response = await authenticatedFetch.fetch(`/api/tasks/${taskId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subtasks: mergedSubtasks }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = (body as { error?: string }).error ?? `Request failed: ${response.status}`;
      throw new Error(msg);
    }

    const data: TaskResponse = await response.json();
    return data.task;
  }
}
